/**
 * Global Filter Context - Provides account, environment, and time filtering across all pages
 *
 * This context manages global filters that persist across navigation and can be
 * applied to any page that supports these filter parameters.
 *
 * Key behaviors:
 * - Empty arrays mean "no filter applied" (show all)
 * - Values in arrays mean "filter to these specific values"
 * - Filters persist across navigation via localStorage
 * - Pages can choose to use or ignore global filters
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router'

import type { GlobalFilterFacetAccount } from '../services/globalFilterFacetsService'
import { fetchGlobalFilterFacets } from '../services/globalFilterFacetsService'
import type {
  GlobalTimeFilter,
  TimeFilterPreset,
  TimestampMode,
  TimezoneMode,
} from '../types/filterTypes'
import { TIME_FILTER_PRESET_OFFSETS_MS } from '../types/filterTypes'
import { getAppPrefs, setAppPrefs } from '../utils/prefs'

import { useTenant } from './TenantContext'
import {
  readFilterParamsFromURL,
  writeFilterParamsToURL,
} from './urlFilterParams'

export type GlobalFilterState = {
  accountId: string[]
  environment: string[]
  timeFilter: GlobalTimeFilter
  timestampMode: TimestampMode
  timezoneMode: TimezoneMode
  // PENG-14090 — true once the user has actually picked "Most recent activity"
  // or "Earliest activity" in the TimeRangeFilterControl popover (i.e.
  // `setTimestampMode` has run). While false, consumers that want a different
  // page-level default (see `useEffectiveTimestampMode`) are free to override;
  // the global `timestampMode` field keeps its last_seen fallback so any
  // reader that doesn't know about per-page defaults still sees the historical
  // value. Optional so test mocks of GlobalFilterState don't all need to be
  // updated — missing is treated as false.
  timestampModeExplicit?: boolean
}

type GlobalFilterFacets = {
  accountIds: string[]
  environments: string[]
  // accounts carries the per-account label/environment/hasResources records
  // the dropdown needs (PENG-13403 PM follow-up: sort by resource count +
  // flag zero-resource accounts). Backend-sorted by resource count
  // descending — always iterate this array in place, never re-sort it.
  accounts: GlobalFilterFacetAccount[]
}

type GlobalFilterContextType = {
  filters: GlobalFilterState
  facets: GlobalFilterFacets | null
  loading: boolean
  error: string | null
  updateFilter: (
    key: keyof Pick<GlobalFilterState, 'accountId' | 'environment'>,
    value: string[],
  ) => void
  clearAllFilters: () => void
  refreshFacets: () => Promise<void>
  hasActiveFilters: boolean
  // Time filter
  setTimeFilter: (tf: GlobalTimeFilter) => void
  clearTimeFilter: () => void
  hasActiveTimeFilter: boolean
  resolveTimeFilter: () => { after: string | null; before: string | null }
  // Timestamp mode (first_seen / last_seen) and timezone (utc / local)
  setTimestampMode: (mode: TimestampMode) => void
  setTimezoneMode: (mode: TimezoneMode) => void
}

const GLOBAL_FILTERS_STORAGE_KEY = 'global_filters'

const defaultFilters: GlobalFilterState = {
  accountId: [],
  environment: [],
  timeFilter: { mode: 'none' },
  timestampMode: 'last_seen',
  timezoneMode: 'utc',
  timestampModeExplicit: false,
}

const GlobalFilterContext = createContext<GlobalFilterContextType | null>(null)

type GlobalFilterProviderProps = {
  children: React.ReactNode
}

export const GlobalFilterProvider = ({
  children,
}: GlobalFilterProviderProps): React.JSX.Element => {
  const { selectedTenant } = useTenant()
  const location = useLocation()
  const [filters, setFilters] = useState<GlobalFilterState>(() => {
    const saved = getAppPrefs<GlobalFilterState>(GLOBAL_FILTERS_STORAGE_KEY)
    const savedTimeFilter = saved?.timeFilter ?? { mode: 'none' as const }
    // Clamp shift to prevent overflow from corrupt persisted state
    const timeFilter: GlobalTimeFilter =
      savedTimeFilter.mode === 'preset' &&
      typeof savedTimeFilter.shift === 'number'
        ? {
            ...savedTimeFilter,
            shift: Math.max(0, Math.min(365, savedTimeFilter.shift)),
          }
        : savedTimeFilter
    // URL reflection (tech-spec.md §9): account_id/environment present in the
    // URL win over localStorage on mount (a copy-pasted URL, back/forward
    // navigation, or an inbound deep-link should reproduce the filter set).
    // Missing URL params fall back to localStorage as before.
    const urlParams = readFilterParamsFromURL(window.location.search)
    return {
      accountId: urlParams?.accountId ?? saved?.accountId ?? [],
      environment: urlParams?.environment ?? saved?.environment ?? [],
      timeFilter,
      timestampMode: saved?.timestampMode ?? 'last_seen',
      timezoneMode: saved?.timezoneMode ?? 'utc',
      // PENG-14090 — persisted explicit flag. Missing in persisted state (pre-
      // PENG-14090 install) coerces to false, so the catalog's page-level
      // default takes effect for returning users who never touched the toggle.
      timestampModeExplicit: saved?.timestampModeExplicit ?? false,
    }
  })
  const [facets, setFacets] = useState<GlobalFilterFacets | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateFilter = useCallback(
    (
      key: keyof Pick<GlobalFilterState, 'accountId' | 'environment'>,
      value: string[],
    ) => {
      setFilters(prev => {
        const newFilters = {
          ...prev,
          [key]: value,
        }
        setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
        writeFilterParamsToURL({
          accountId: newFilters.accountId,
          environment: newFilters.environment,
        })
        return newFilters
      })
    },
    [],
  )

  const setTimeFilter = useCallback((tf: GlobalTimeFilter) => {
    setFilters(prev => {
      const newFilters = { ...prev, timeFilter: tf }
      setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
      return newFilters
    })
  }, [])

  const clearTimeFilter = useCallback(() => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        timeFilter: { mode: 'none' } as GlobalTimeFilter,
      }
      setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
      return newFilters
    })
  }, [])

  const setTimestampMode = useCallback((mode: TimestampMode) => {
    setFilters(prev => {
      // PENG-14090 — any click on the popover's "Filter by" toggle (regardless
      // of which mode) marks the choice as explicit, so a page-level default
      // (e.g. ComponentCatalogPage's 'first_seen') steps aside and the user's
      // pick wins everywhere from then on.
      const newFilters = {
        ...prev,
        timestampMode: mode,
        timestampModeExplicit: true,
      }
      setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
      return newFilters
    })
  }, [])

  const setTimezoneMode = useCallback((mode: TimezoneMode) => {
    setFilters(prev => {
      const newFilters = { ...prev, timezoneMode: mode }
      setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
      return newFilters
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters)
    setAppPrefs(defaultFilters, GLOBAL_FILTERS_STORAGE_KEY)
    writeFilterParamsToURL({ accountId: [], environment: [] })
  }, [])

  // Tracks the in-flight facets request so a newer tenant fetch (or unmount)
  // can abort a stale one — otherwise a slow response for a previous tenant
  // could overwrite fresher facets/error/loading state after it landed.
  const facetsAbortRef = useRef<AbortController | null>(null)

  const refreshFacets = useCallback(async () => {
    if (!selectedTenant) return

    // Abort any previous request before starting a newer one.
    facetsAbortRef.current?.abort()
    const controller = new AbortController()
    facetsAbortRef.current = controller

    setLoading(true)
    setError(null)

    try {
      // Tenant-scoped facets endpoint (tech-spec.md §11) — computes distinct
      // accounts + environments server-side in one round-trip instead of
      // paging listCloudAccounts (up to 1000 rows) and deriving distinct
      // values client-side.
      const response = await fetchGlobalFilterFacets(
        selectedTenant.id,
        controller.signal,
      )

      // A newer request (or unmount) superseded this one — drop the result so
      // it can't clobber fresher state.
      if (controller.signal.aborted) return

      setFacets({
        accountIds: response.accountIds,
        environments: response.environments,
        accounts: response.accounts ?? [],
      })
    } catch (err) {
      if (controller.signal.aborted) return

      console.error('Failed to fetch global filter facets:', err)
      setError(
        err instanceof Error ? err.message : 'Failed to load filter options',
      )

      // Fallback to empty facets
      setFacets({
        accountIds: [],
        environments: [],
        accounts: [],
      })
    } finally {
      // Leave loading true when superseded — the newer request owns it now.
      if (!controller.signal.aborted) setLoading(false)
    }
  }, [selectedTenant])

  // Load facets when tenant changes; abort any in-flight request on tenant
  // change or unmount so it can't update state after a newer fetch has started.
  useEffect(() => {
    refreshFacets()
    return () => {
      facetsAbortRef.current?.abort()
    }
  }, [refreshFacets])

  // Validate saved account/environment filters against current tenant facets
  useEffect(() => {
    if (facets) {
      const validAccountIds = filters.accountId.filter(id =>
        facets.accountIds.includes(id),
      )
      const validEnvironments = filters.environment.filter(env =>
        facets.environments.includes(env),
      )

      if (
        validAccountIds.length !== filters.accountId.length ||
        validEnvironments.length !== filters.environment.length
      ) {
        const newFilters = {
          ...filters,
          accountId: validAccountIds,
          environment: validEnvironments,
        }
        setFilters(newFilters)
        setAppPrefs(newFilters, GLOBAL_FILTERS_STORAGE_KEY)
        writeFilterParamsToURL({
          accountId: validAccountIds,
          environment: validEnvironments,
        })
      }
    }
  }, [facets, filters.accountId, filters.environment]) // eslint-disable-line react-hooks/exhaustive-deps

  // URL reflection — route-change reconciliation (tech-spec.md §9). React
  // Router's navigate() doesn't rewrite the URL's account_id/environment
  // params on its own; if the current URL is missing them but state still
  // has an active filter, mirror the state back into the URL so navigating
  // away from a deep-link (or between pages generally) doesn't silently
  // drop the filter from the address bar.
  useEffect(() => {
    const current = readFilterParamsFromURL(window.location.search)
    const hasActiveAccountOrEnv =
      filters.accountId.length > 0 || filters.environment.length > 0
    if (current === null && hasActiveAccountOrEnv) {
      writeFilterParamsToURL({
        accountId: filters.accountId,
        environment: filters.environment,
      })
    }
    // Only re-run when the route changes — this effect exists to reconcile
    // the URL on navigation, not on every filter tick (updateFilter already
    // writes the URL directly on every change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const hasActiveFilters = useMemo(() => {
    return filters.accountId.length > 0 || filters.environment.length > 0
  }, [filters.accountId.length, filters.environment.length])

  const hasActiveTimeFilter = useMemo(
    () => filters.timeFilter.mode !== 'none',
    [filters.timeFilter.mode],
  )

  // Resolves the current time filter to UTC ISO-8601 strings at call-time.
  // For presets, timestamps are computed from Date.now() so rolling windows stay fresh.
  // shift=0 (default): [now-duration, now]
  // shift=N: [now-(N+1)*duration, now-N*duration]  — N intervals back from current window
  const resolveTimeFilter = useCallback((): {
    after: string | null
    before: string | null
  } => {
    const tf = filters.timeFilter
    if (tf.mode === 'none') return { after: null, before: null }
    if (tf.mode === 'custom') return { after: tf.start, before: tf.end }
    const shift = tf.shift ?? 0
    const duration =
      TIME_FILTER_PRESET_OFFSETS_MS[tf.preset as TimeFilterPreset]
    const now = Date.now()
    const after = new Date(now - (shift + 1) * duration)
    const before = shift > 0 ? new Date(now - shift * duration) : null
    return { after: after.toISOString(), before: before?.toISOString() ?? null }
  }, [filters.timeFilter])

  const contextValue = useMemo(
    (): GlobalFilterContextType => ({
      filters,
      facets,
      loading,
      error,
      updateFilter,
      clearAllFilters,
      refreshFacets,
      hasActiveFilters,
      setTimeFilter,
      clearTimeFilter,
      hasActiveTimeFilter,
      resolveTimeFilter,
      setTimestampMode,
      setTimezoneMode,
    }),
    [
      filters,
      facets,
      loading,
      error,
      updateFilter,
      clearAllFilters,
      refreshFacets,
      hasActiveFilters,
      setTimeFilter,
      clearTimeFilter,
      hasActiveTimeFilter,
      resolveTimeFilter,
      setTimestampMode,
      setTimezoneMode,
    ],
  )

  return (
    <GlobalFilterContext.Provider value={contextValue}>
      {children}
    </GlobalFilterContext.Provider>
  )
}

export const useGlobalFilter = (): GlobalFilterContextType => {
  const context = useContext(GlobalFilterContext)
  if (!context) {
    throw new Error(
      'useGlobalFilter must be used within a GlobalFilterProvider',
    )
  }
  return context
}
